import { DateField } from "@fawkes/ui";
import { useState, type Dispatch, type SetStateAction } from "react";
import { Info, PawPrint, UserRound } from "lucide-react";

import type { Context } from "../pensionklant/hulp";
import { KlantBalk, Terug, Voet, Volgende, isDatum, langeDatum, useKlantDieren, type GekozenKlant, type Vertaal } from "./bouwstenen";
import { antwoordSleutel, eigenElementen, openVragen, type Antwoorden, type WizardElement, type WizardStap } from "./indeling";

const TEKST_MAX = 500;
const LANG_MAX = 2000;

/** Voor een stap die buiten de wizard getoond wordt, zonder eigen vragen. */
export const GEEN_ANTWOORDEN: Antwoorden = {};
export const NIETS: Dispatch<SetStateAction<Antwoorden>> = () => {};

/** Een antwoord zoals je het leest: Ja of Nee, de keuzes achter elkaar, een datum voluit. */
export function antwoordTekst(type: string | null, waarde: string, t: Vertaal, locale: string): string {
  if (type === "BOOLEAN") return t(waarde === "true" ? "nieuweReservering.verblijf.ja" : "nieuweReservering.verblijf.nee");
  if (type === "MULTISELECT") return lijst(waarde).join(", ");
  if (type === "DATE" && isDatum(waarde)) return langeDatum(waarde, locale);
  return waarde;
}

function lijst(waarde: string): string[] {
  try {
    const x: unknown = JSON.parse(waarde);
    return Array.isArray(x) ? x.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Het invoerveld van een eigen vraag. Het antwoord is altijd tekst: ja/nee als "true"/"false", een datum als
 * jjjj-mm-dd en meerdere keuzes als JSON-lijst, zoals de api het bewaart.
 */
export function VraagInvoer({
  vraag,
  label,
  waarde,
  onChange,
  t,
  fout,
  uit,
}: {
  vraag: WizardElement;
  label: string;
  waarde: string;
  onChange: (waarde: string) => void;
  t: Vertaal;
  fout?: boolean;
  uit?: boolean;
}) {
  const gemeen = { "aria-label": label, "data-fout": fout || undefined, disabled: uit };
  switch (vraag.type) {
    case "TEXTAREA":
      return (
        <textarea className="nr-invoer nr-invoer--hoog" maxLength={LANG_MAX} value={waarde} onChange={(e) => onChange(e.target.value)} {...gemeen} />
      );
    case "NUMBER":
      return <input className="nr-invoer" type="number" step="any" inputMode="decimal" value={waarde} onChange={(e) => onChange(e.target.value)} {...gemeen} />;
    case "DATE":
      return <DateField className="nr-invoer" value={waarde} onChange={(e) => onChange(e.target.value)} {...gemeen} />;
    case "BOOLEAN":
      return (
        <div className="nr-radios nr-radios--rij" role="radiogroup" aria-label={label} data-fout={fout || undefined}>
          {(["true", "false"] as const).map((w) => (
            <label key={w} className="nr-radio">
              <input type="radio" checked={waarde === w} disabled={uit} onChange={() => onChange(w)} />
              <span>{t(w === "true" ? "nieuweReservering.verblijf.ja" : "nieuweReservering.verblijf.nee")}</span>
            </label>
          ))}
        </div>
      );
    case "SELECT":
      return (
        <select className="nr-invoer" value={waarde} onChange={(e) => onChange(e.target.value)} {...gemeen}>
          <option value="">{t("nieuweReservering.eigen.kies")}</option>
          {(vraag.opties ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case "MULTISELECT": {
      const gekozen = lijst(waarde);
      return (
        <div className="nr-vinklijst" role="group" aria-label={label} data-fout={fout || undefined}>
          {(vraag.opties ?? []).map((o) => (
            <label key={o} className="nr-vink">
              <input
                type="checkbox"
                checked={gekozen.includes(o)}
                disabled={uit}
                onChange={(e) =>
                  onChange(JSON.stringify(e.target.checked ? [...gekozen, o] : gekozen.filter((x) => x !== o)))
                }
              />
              {o}
            </label>
          ))}
        </div>
      );
    }
    default:
      return <input className="nr-invoer" maxLength={TEKST_MAX} value={waarde} onChange={(e) => onChange(e.target.value)} {...gemeen} />;
  }
}

/** Een vraag met zijn label, uitleg en ster. */
export function VraagVeld({
  vraag,
  label,
  waarde,
  onChange,
  t,
  fout,
  uit,
}: {
  vraag: WizardElement;
  label?: string;
  waarde: string;
  onChange: (waarde: string) => void;
  t: Vertaal;
  fout?: boolean;
  uit?: boolean;
}) {
  const tekst = label ?? vraag.titel ?? "";
  return (
    <div className="nr-veld nr-vraag">
      <span className="nr-veld__label" aria-hidden="true">
        {tekst}
        {vraag.verplicht && <span className="nr-veld__ster">{" *"}</span>}
      </span>
      {vraag.uitleg && <span className="nr-klein">{vraag.uitleg}</span>}
      <VraagInvoer vraag={vraag} label={tekst} waarde={waarde} onChange={onChange} t={t} fout={fout} uit={uit} />
    </div>
  );
}

export function TekstBlok({ titel, tekst }: { titel?: string | null; tekst?: string | null }) {
  return (
    <div className="nr-info">
      <Info size={20} aria-hidden="true" />
      <span className="nr-info__tekst">
        {titel && <strong>{titel}</strong>}
        {tekst && <span>{tekst}</span>}
      </span>
    </div>
  );
}

/**
 * De eigen vragen en tekstblokken van een stap, in de volgorde van de indeling. Een vraag per dier krijgt een veld
 * per gekozen dier.
 */
export function EigenElementen({
  elementen,
  dieren,
  antwoorden,
  setAntwoorden,
  t,
  geprobeerd,
  uit,
}: {
  elementen: WizardElement[];
  dieren: { id: number; naam: string | null }[];
  antwoorden: Antwoorden;
  setAntwoorden: Dispatch<SetStateAction<Antwoorden>>;
  t: Vertaal;
  geprobeerd?: boolean;
  uit?: boolean;
}) {
  if (elementen.length === 0) return null;
  const zet = (sleutel: string, waarde: string) => setAntwoorden((x) => ({ ...x, [sleutel]: waarde }));
  const leeg = (sleutel: string) => {
    const w = antwoorden[sleutel];
    return w == null || w.trim() === "" || w === "[]";
  };
  return (
    <div className="nr-eigen">
      {elementen.map((e) => {
        if (e.soort === "TEKST") return <TekstBlok key={e.id} titel={e.titel} tekst={e.uitleg} />;
        if (e.soort !== "VRAAG") return null;
        if (!e.perDier) {
          return (
            <section key={e.id} className="nr-eigen__vraag">
              <VraagVeld
                vraag={e}
                waarde={antwoorden[e.id] ?? ""}
                onChange={(w) => zet(e.id, w)}
                t={t}
                fout={geprobeerd && e.verplicht && leeg(e.id)}
                uit={uit}
              />
            </section>
          );
        }
        return (
          <section key={e.id} className="nr-eigen__vraag" aria-label={e.titel ?? undefined}>
            <span className="nr-veld__label">
              {e.titel}
              {e.verplicht && <span className="nr-veld__ster">{" *"}</span>}
            </span>
            {e.uitleg && <span className="nr-klein">{e.uitleg}</span>}
            <div className="nr-eigen__dieren">
              {dieren.map((d) => {
                const sleutel = antwoordSleutel(e.id, d.id);
                const naam = d.naam ?? String(d.id);
                return (
                  <div key={d.id} className="nr-eigen__dier">
                    <span className="nr-regel">
                      <PawPrint size={15} aria-hidden="true" />
                      <strong>{naam}</strong>
                    </span>
                    <VraagInvoer
                      vraag={e}
                      label={`${e.titel} – ${naam}`}
                      waarde={antwoorden[sleutel] ?? ""}
                      onChange={(w) => zet(sleutel, w)}
                      t={t}
                      fout={geprobeerd && e.verplicht && leeg(sleutel)}
                      uit={uit}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * Verder mag pas als de verplichte eigen vragen beantwoord zijn. Een klik op Volgende met open vragen laat zien
 * welke; daarna gaat de knop weer gewoon door.
 */
export function useVerplicht(stap: WizardStap | undefined, antwoorden: Antwoorden, dierIds: number[]) {
  const [geprobeerd, setGeprobeerd] = useState(false);
  const open = openVragen(stap, antwoorden, dierIds);
  return {
    geprobeerd,
    open,
    verder: (onVolgende: () => void) => () => {
      if (open.length > 0) setGeprobeerd(true);
      else onVolgende();
    },
  };
}

export function VerplichtMelding({ open, geprobeerd, t }: { open: WizardElement[]; geprobeerd: boolean; t: Vertaal }) {
  if (!geprobeerd || open.length === 0) return null;
  return (
    <p className="nr-melding" role="alert">
      {t("nieuweReservering.eigen.verplicht", { args: [open.map((e) => e.titel).join(", ")] })}
    </p>
  );
}

/** Een eigen stap uit de indeling: alleen eigen vragen en teksten. */
export function EigenStap({
  klant,
  stap,
  dierIds,
  antwoorden,
  setAntwoorden,
  ctx,
  onKlant,
  onTerug,
  onVolgende,
}: {
  klant: GekozenKlant;
  stap: WizardStap;
  dierIds: number[];
  antwoorden: Antwoorden;
  setAntwoorden: Dispatch<SetStateAction<Antwoorden>>;
  ctx: Context;
  onKlant: () => void;
  onTerug: () => void;
  onVolgende: () => void;
}) {
  const t = ctx.t as Vertaal;
  const { dieren } = useKlantDieren(klant.id);
  const gekozen = dieren.filter((d) => dierIds.includes(d.id));
  const verplicht = useVerplicht(stap, antwoorden, dierIds);
  return (
    <>
      <div className="nr__inhoud">
        <KlantBalk klant={klant} knop={t("nieuweReservering.periode.klantBekijken")} knopIcoon={<UserRound size={16} aria-hidden="true" />} onKnop={onKlant} />
        <h3 className="nr-kaart__titel">{stap.titel}</h3>
        {stap.uitleg && <p className="nr-kaart__uitleg">{stap.uitleg}</p>}
        <EigenElementen
          elementen={eigenElementen(stap)}
          dieren={gekozen}
          antwoorden={antwoorden}
          setAntwoorden={setAntwoorden}
          t={t}
          geprobeerd={verplicht.geprobeerd}
        />
        <VerplichtMelding open={verplicht.open} geprobeerd={verplicht.geprobeerd} t={t} />
      </div>
      <Voet links={<Terug t={t} onClick={onTerug} />} rechts={<Volgende t={t} onClick={verplicht.verder(onVolgende)} />} />
    </>
  );
}
